const safeString = (value) => (String(value || '')).trim();

export const dateFormatter = (datetime) => new Date(datetime).toLocaleDateString(
  'en-gb',
  {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  },
);

export const timeFormatter = (datetime) => new Date(datetime).toLocaleTimeString('en-gb');

export const timeFormatter2digit = (datetime) => `
  ${new Date(datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}`;

export const serviceAnnouncementDatetime = (datetime) => `${dateFormatter(datetime)} ${timeFormatter2digit(datetime)}`;

export const dateValue = (value, defaultValue = null) => {
  if (!value) { return defaultValue; }
  const parsedDate = Date.parse(safeString(value));

  if (!Number.isNaN(parsedDate)) { return new Date(parsedDate); }
  return defaultValue;
};

export const dateLiteral = (value, defaultValue = null) => {
  const lowered = safeString(value).toLowerCase();
  if (lowered === 'today') { return new Date(); }
  if (lowered === 'yesterday') {
    const yesterday = new Date();
    yesterday.setDate(new Date().getDate() - 1);
    return yesterday;
  }
  if (lowered === 'tomorrow') {
    const tomorrow = new Date();
    tomorrow.setDate(new Date().getDate() + 1);
    return tomorrow;
  }
  return defaultValue;
};

const parseDate = (text) => dateValue(text) || dateLiteral(text);

// This method uses 'en-US' in order to get the date result to be as follows:
// Month/Day/Year/Time e.g. June 1, 2020 at 11:16am
// Be aware that different timezones (like 'en-GB') will give different formats.
export const formatDate = (date, options = {
  month: 'long',
  year: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
}) => new Intl.DateTimeFormat('en-US', options).format(new Date(date));

export const localToUTCDate = (date) => (
  new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
  )
);

export const localToUTCDatetime = (date) => (
  new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
  )
);

export const formatDateForState = (date) => date.toISOString();

export const relativeDate = (date, intl, prefix = '') => {
  const diffSeconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);

  const minute = 60;
  const hour = minute * 60;
  const day = hour * 24;
  const week = day * 7;
  const month = day * 30;
  const year = day * 365;

  let message;
  let messageArgs;

  if (diffSeconds < minute) {
    message = 'date.just.now';
  } else if (diffSeconds < 2 * minute) {
    message = 'date.last.minute';
  } else if (diffSeconds < hour) {
    message = 'date.minutes.ago';
    messageArgs = { minutes: Math.floor(diffSeconds / minute) };
  } else if (diffSeconds < 2 * hour) {
    message = 'date.last.hour';
  } else if (diffSeconds < day) {
    message = 'date.hours.ago';
    messageArgs = { hours: Math.floor(diffSeconds / hour) };
  } else if (diffSeconds < 2 * day) {
    message = 'date.yesterday';
  } else if (diffSeconds < week) {
    message = 'date.days.ago';
    messageArgs = { days: Math.floor(diffSeconds / day) };
  } else if (diffSeconds < 2 * week) {
    message = 'date.last.week';
  } else if (diffSeconds < 5 * week) {
    message = 'date.weeks.ago';
    messageArgs = { weeks: Math.floor(diffSeconds / week) };
  } else if (diffSeconds < 2 * month) {
    message = 'date.last.month';
  } else if (diffSeconds < year) {
    message = 'date.months.ago';
    messageArgs = { months: Math.floor(diffSeconds / month) };
  } else if (diffSeconds < 2 * year) {
    message = 'date.last.year';
  } else if (diffSeconds < 3 * year) {
    message = 'date.years.ago';
    messageArgs = { years: Math.floor(diffSeconds / year) };
  } else {
    message = 'date.many.years.ago';
    messageArgs = { years: Math.floor(diffSeconds / year) };
  }

  return intl.formatMessage({ id: `${prefix}${message}` }, messageArgs);
};

export default parseDate;
